import { useAtomValue } from "@effect/atom-react";
import * as Schema from "effect/Schema";
import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";

import { isElectron } from "../env";
import { getLocalStorageItem, removeLocalStorageItem } from "../hooks/useLocalStorage";
import { onDeskCommand } from "../deskCommandBus";
import { resolveShortcutCommand, shortcutLabelForCommand } from "../keybindings";
import { cn } from "../lib/utils";
import { primaryServerKeybindingsAtom } from "../state/server";
import { useEnvironmentIdentificationMode } from "../hooks/useSettings";
import { SettingsSidebarNav } from "./settings/SettingsSidebarNav";
import { DesktopUpdateAnnouncer } from "./DesktopUpdateAnnouncer";
import { SidebarChromeHeader } from "./sidebar/SidebarChrome";
import {
  resolveSidebarStageFocusRingOffsetClass,
  useSidebarStageBackdropVariant,
} from "./SidebarStageBackdrop";
import { useProjects } from "../state/entities";
import {
  resolveInitialThreadSidebarWidth,
  resolveThreadSidebarMaximumWidth,
  THREAD_MAIN_CONTENT_MIN_WIDTH,
  THREAD_SIDEBAR_MIN_WIDTH,
  THREAD_SIDEBAR_WIDTH_STORAGE_KEY,
} from "./threadSidebarWidth";
import {
  Sidebar,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
  useSidebarVisibility,
} from "./ui/sidebar";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";
import { MACOS_TRAFFIC_LIGHTS_LEFT_INSET, useMacosTrafficLights } from "../workspaceTitlebar";
import { useNeucleosRoomBoundary } from "./NeucleosRoomFrame";
import { readStatusRefreshingWorlds, startNeucleosSignIn } from "../neucleos/loginCeremony";
import { requestNeucleosSignOut } from "./settings/AccountSession";
import {
  refreshNeucleosWorlds,
  signInEndedNotice,
  signInEndingNotice,
  useNeucleosWorldStore,
} from "../neucleos/worldStore";
import { toastManager } from "./ui/toast";

function subscribeToViewportWidth(onChange: () => void): () => void {
  window.addEventListener("resize", onChange);
  return () => window.removeEventListener("resize", onChange);
}

function readViewportWidth(): number {
  return window.innerWidth;
}

function readInitialThreadSidebarWidth(): number {
  try {
    return resolveInitialThreadSidebarWidth(
      getLocalStorageItem(THREAD_SIDEBAR_WIDTH_STORAGE_KEY, Schema.Finite),
      window.innerWidth,
    );
  } catch (error) {
    console.error("Could not read persisted thread sidebar width.", error);
    return resolveInitialThreadSidebarWidth(null, window.innerWidth);
  }
}

function SidebarControl() {
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const { toggleSidebar } = useSidebar();
  const isSidebarVisible = useSidebarVisibility();
  const environmentIdentificationMode = useEnvironmentIdentificationMode();
  const stageBackdropVariant = useSidebarStageBackdropVariant(
    environmentIdentificationMode === "artwork",
  );
  const shortcutLabel = shortcutLabelForCommand(keybindings, "sidebar.toggle");

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (
        event.target instanceof HTMLElement &&
        event.target.closest("[data-keybinding-capture]")
      ) {
        return;
      }
      if (resolveShortcutCommand(event, keybindings) !== "sidebar.toggle") return;

      event.preventDefault();
      event.stopPropagation();
      toggleSidebar();
    };

    // Capture before focused editors consume commands such as Mod+B for rich-text formatting.
    window.addEventListener("keydown", onKeyDown, true);
    // HIG8: the Mac View menu's Show/Hide Room Rail arrives as a desk command.
    const stopMenuCommand = onDeskCommand("toggle-room-rail", toggleSidebar);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      stopMenuCommand();
    };
  }, [keybindings, toggleSidebar]);

  return (
    // The right-side layout controls carry mr-px (border compensation inside
    // the panel), so the trigger mirrors it: both clusters sit one extra pixel
    // off their edge and the titlebar reads symmetric.
    <div
      className="pointer-events-none fixed left-[var(--workspace-controls-left)] top-[var(--workspace-controls-top)] z-50 ml-px flex h-[var(--workspace-topbar-height)] items-center"
      data-sidebar-control=""
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <SidebarTrigger
              className={cn(
                "pointer-events-auto",
                isSidebarVisible &&
                  stageBackdropVariant &&
                  "focus-visible:ring-white/90 [&_svg]:stroke-white/90! [&_svg]:opacity-100! [&_svg]:hover:stroke-white! [:hover,[data-pressed]]:bg-white/15",
                isSidebarVisible &&
                  stageBackdropVariant &&
                  resolveSidebarStageFocusRingOffsetClass(stageBackdropVariant),
              )}
              aria-label="Toggle main sidebar"
            />
          }
        />
        <TooltipPopup side="bottom">
          Toggle main sidebar{shortcutLabel ? ` (${shortcutLabel})` : ""}
        </TooltipPopup>
      </Tooltip>
    </div>
  );
}

// Settings swaps the thread sidebar out of the tree. Keep the lightweight
// project projection subscribed so returning to a draft never renders the
// zero-project state while the environment snapshot reconnects.
function ProjectProjectionRetention() {
  useProjects();
  return null;
}

// W-2308dc50: a few days before this Mac's sign-in reaches its fixed end
// (90 days; the Desk renews it until then), say so once per end date, so the
// person can sign in again at a quiet moment instead of finding it ended.
function SignInEndingNotice() {
  const signedIn = useNeucleosWorldStore((state) => state.signedIn);
  const endsAt = useNeucleosWorldStore((state) => state.signInEndsAt);
  const shown = useRef<number | null>(null);
  // A Desk left open for weeks still learns the date: re-read the status
  // every six hours (a bounded interval, far below setTimeout's limit).
  useEffect(() => {
    const timer = setInterval(() => void refreshNeucleosWorlds(), 6 * 60 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    const description = signedIn ? signInEndingNotice(endsAt) : null;
    if (description === null || shown.current === endsAt) return;
    shown.current = endsAt;
    toastManager.add({ type: "warning", title: "Sign in again soon", description });
  }, [signedIn, endsAt]);
  return null;
}

// W-4a6095af (Can 31): when the sign-in on this Mac ended without the person
// signing out (the World stopped accepting it), the main screen says so in plain
// words with Sign in again, at load as well as live, and keeps saying so until
// the person is signed in. With no World left the window shows the Local view,
// which has no room card to carry the sentence.
export function SignInEndedNotice() {
  const notice = useNeucleosWorldStore(signInEndedNotice);
  const stopWatch = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (notice === null) return;
    // One sign-in start at a time (adversary P3): clicks while one is starting
    // are ignored, and a start that finishes after the notice went away stops
    // its own watcher, so none is left behind.
    let active = true;
    let starting = false;
    const toastId = toastManager.add({
      type: "warning",
      title: "Signed out",
      description: notice,
      timeout: 0,
      actionProps: {
        children: "Sign in again",
        onClick: () => {
          if (starting) return;
          starting = true;
          stopWatch.current?.();
          stopWatch.current = null;
          void startNeucleosSignIn({
            read: readStatusRefreshingWorlds,
            onDone: () => {
              stopWatch.current = null;
            },
          })
            .then((stop) => {
              if (!active) stop?.();
              else stopWatch.current = stop;
            })
            .catch(() => undefined)
            .finally(() => {
              starting = false;
            });
        },
      },
    });
    return () => {
      active = false;
      toastManager.close(toastId);
      stopWatch.current?.();
      stopWatch.current = null;
    };
  }, [notice]);
  return null;
}

export function AppSidebarLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  // The thread sidebar is retired (principal directive 2026-08-28): the room
  // is the navigation — the Cleo Panel is the left column, tabs are the
  // sessions, Add project lives in the palette and the unbound panel. The
  // sidebar shell survives ONLY as the routed Settings pages' nav column.
  const pathname = useLocation({ select: (location) => location.pathname });
  const isOnSettings = pathname === "/settings" || pathname.startsWith("/settings/");
  const showsMacosTrafficLights = useMacosTrafficLights();
  const sessionBoundary = useNeucleosRoomBoundary();
  const [sidebarWidth, setSidebarWidth] = useState(readInitialThreadSidebarWidth);
  const accountActionPending = useRef(false);
  const stopAccountWatch = useRef<(() => void) | null>(null);
  const accountActionsMounted = useRef(true);
  useEffect(() => {
    accountActionsMounted.current = true;
    return () => {
      accountActionsMounted.current = false;
      stopAccountWatch.current?.();
    };
  }, []);
  // Subscribed rather than read once: the clamp must track live window size,
  // and a clamped drag ends with an unchanged width, which skips the re-render
  // that would otherwise refresh a render-time snapshot.
  const viewportWidth = useSyncExternalStore(subscribeToViewportWidth, readViewportWidth);
  const sidebarMaximumWidth = resolveThreadSidebarMaximumWidth(viewportWidth);
  const resetSidebarWidth = () => {
    try {
      removeLocalStorageItem(THREAD_SIDEBAR_WIDTH_STORAGE_KEY);
    } catch (error) {
      console.error("Could not clear persisted thread sidebar width.", error);
    }
    setSidebarWidth(resolveInitialThreadSidebarWidth(null, viewportWidth));
  };
  const sidebarProviderStyle = {
    "--sidebar-width": `${sidebarWidth}px`,
    ...(showsMacosTrafficLights
      ? { "--workspace-controls-left": MACOS_TRAFFIC_LIGHTS_LEFT_INSET }
      : {}),
  } as CSSProperties;

  useEffect(() => {
    const onMenuAction = window.desktopBridge?.onMenuAction;
    if (typeof onMenuAction !== "function") {
      return;
    }

    const unsubscribe = onMenuAction((action) => {
      if (action === "open-settings") {
        const isSettingsRoute = /^\/settings(\/|$)/.test(pathname);
        if (!isSettingsRoute) {
          void navigate({ to: "/settings" });
        }
        return;
      }
      if (
        ![
          "open-account",
          "neucleos-sign-in",
          "neucleos-refresh-access",
          "neucleos-sign-out",
        ].includes(action)
      )
        return;

      void navigate({ to: "/settings/account" });
      if (action === "open-account") return;
      // Signing out of Neucleos always asks first, in Account (AccountSession.tsx, #1450).
      if (action === "neucleos-sign-out") {
        requestNeucleosSignOut();
        return;
      }
      if (accountActionPending.current) {
        toastManager.add({
          type: "info",
          title: "An account action is in progress",
          description: "Try again when the current request finishes.",
        });
        return;
      }
      accountActionPending.current = true;
      void (async () => {
        stopAccountWatch.current?.();
        stopAccountWatch.current = null;
        let settled = false;
        const stop = await startNeucleosSignIn({
          read: readStatusRefreshingWorlds,
          onDone: () => {
            settled = true;
            stopAccountWatch.current = null;
          },
        });
        // Only serialize the launch request. While the sign-in window is open,
        // Sign out must still be able to stop its watcher and use the ordinary logout.
        accountActionPending.current = false;
        if (!stop) throw new Error("Sign-in could not start.");
        if (!accountActionsMounted.current || settled) stop();
        else stopAccountWatch.current = stop;
      })().catch(() => {
        accountActionPending.current = false;
        if (accountActionsMounted.current) {
          toastManager.add({
            type: "error",
            title: "Neucleos account needs attention",
            description: "The account action could not finish. Try again from Account settings.",
          });
        }
      });
    });

    return () => {
      unsubscribe?.();
    };
  }, [navigate, pathname]);

  return (
    // h-full, not h-dvh: the World rail above this shell owns part of the
    // viewport; this provider fills whatever height remains.
    <SidebarProvider className="h-full! min-h-0!" defaultOpen style={sidebarProviderStyle}>
      <ProjectProjectionRetention />
      <SignInEndingNotice />
      <SignInEndedNotice />
      <DesktopUpdateAnnouncer />
      {isOnSettings ? (
        <Sidebar
          side="left"
          collapsible="offcanvas"
          data-app-sidebar=""
          data-cleo-room-root={sessionBoundary?.rootKey ?? undefined}
          className="border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
          resizable={{
            maxWidth: sidebarMaximumWidth,
            minWidth: THREAD_SIDEBAR_MIN_WIDTH,
            shouldAcceptWidth: ({ currentWidth, nextWidth, wrapper }) =>
              nextWidth <= currentWidth ||
              wrapper.clientWidth - nextWidth >= THREAD_MAIN_CONTENT_MIN_WIDTH,
            storageKey: THREAD_SIDEBAR_WIDTH_STORAGE_KEY,
            onResize: setSidebarWidth,
          }}
        >
          <SidebarChromeHeader isElectron={isElectron} />
          <SettingsSidebarNav pathname={pathname} />
          <SidebarRail onDoubleClick={resetSidebarWidth} />
        </Sidebar>
      ) : null}
      {children}
      {isOnSettings ? <SidebarControl /> : null}
    </SidebarProvider>
  );
}
